import {
  createRouter,
  createWebHistory,
  RouteLocationNormalized,
} from 'vue-router'
import SearchPage from '../pages/SearchPage/SearchPage.vue'
import TravelPlanner from '../pages/TravelPlanner/TravelPlanner.vue'
import TravelPassengers from '../pages/TravelDetails/TravelPassengers.vue'
import SearchResultPage from '@/pages/SearchResultPage/SearchResultPage.vue'
import RebookingSearchResultPage from '@/pages/SearchResultPage/RebookingSearchResultPage.vue'
import PaymentPage from '@/pages/PaymentPage/PaymentPage.vue'
import MyPage from '@/pages/MyPagePages/MyPage.vue'
import MyProfil from '@/components/MyPage/MyProfil.vue'
import SeatReservationPage from '../pages/SeatReservationPage.vue'
import EquipmentPage from '@/pages/EquipmentPage/EquipmentPage.vue'
import ResetPassword from '../components/MyPage/ResetPassword.vue'
import PersonalInformationPage from '@/pages/PersonalInformationPage.vue'
import LoginPage from '../pages/MyPagePages/LoginPage.vue'
import SignUpPage from '../pages/MyPagePages/SignUpPage.vue'
import ConfirmationPage from '@/pages/ConfirmationPage.vue'
import TicketPage from '@/pages/MyPagePages/TicketPage.vue'
import GetTicketPage from '@/pages/MyPagePages/GetTicketPage.vue'
import AfterSaleSeats from '@/pages/MyPagePages/AfterSale/AfterSaleSeats.vue'
import AfterSaleCancel from '@/pages/MyPagePages/AfterSale/AfterSaleCancel.vue'
import AfterSaleRebook from '@/pages/MyPagePages/AfterSale/AfterSaleRebook.vue'
import ConfirmAccountPage from '@/pages/MyPagePages/ConfirmAccountPage.vue'
import { useUserStore } from '../stores/userStore'
import { useAuthStore } from '../stores/oauthStore'

const routes = [
  {
    path: '/',
    name: 'Search',
    component: SearchPage,
  },
  {
    path: '/reiseplanlegger',
    name: 'TravelPlanner',
    component: TravelPlanner,
    children: [
      {
        path: 'reisende',
        name: 'TravelPassengers',
        component: TravelPassengers,
      },
      {
        path: 'velg-reise',
        name: 'SearchResultPage',
        component: SearchResultPage,
      },
      {
        path: 'endre-reise',
        name: 'RebookingSearchResultPage',
        component: RebookingSearchResultPage,
      },
      {
        path: 'reserveresete',
        name: 'SeatReservation',
        component: SeatReservationPage,
      },
      {
        path: 'tillegg',
        name: 'EquipmentPage',
        component: EquipmentPage,
      },
      {
        path: 'personalia',
        name: 'PersonalInformation',
        component: PersonalInformationPage,
      },
      {
        path: 'opprett-profil',
        name: 'CreateProfile',
        component: SignUpPage,
      },
      {
        path: 'betaling',
        name: 'payment',
        component: PaymentPage,
        props: (route: RouteLocationNormalized) => ({
          bookingNumber: route.query.bookingNumber,
        }),
      },
      {
        path: '/bekreftelse/:bookingNumber',
        name: 'ConfirmationPage',
        component: ConfirmationPage,
      },
    ],
  },
  {
    path: '/rebooking/reserveresete',
    name: 'RebookingSeatReservation',
    component: SeatReservationPage,
  },
  {
    path: '/rebooking/tillegg',
    name: 'RebookingEquipmentPage',
    component: EquipmentPage,
  },
  {
    path: '/login',
    name: 'Login',
    component: LoginPage,
  },
  {
    path: '/sign-up',
    name: 'SignUp',
    component: SignUpPage,
  },
  {
    path: '/confirm-account',
    name: 'ConfirmAccount',
    component: ConfirmAccountPage,
  },
  {
    path: '/reset-password',
    name: 'ResetPassword',
    component: ResetPassword,
  },
  {
    path: '/min-side',
    component: MyPage,
    children: [
      {
        path: '',
        redirect: { name: 'UserInfo' },
      },
      {
        path: 'brukerinformasjon',
        name: 'UserInfo',
        component: MyProfil,
      },
      {
        path: 'bookings',
        name: 'MyBookings',
        component: MyProfil,
      },
      {
        path: 'bookings/:bookingNumber/seats',
        name: 'AfterSaleSeats',
        component: AfterSaleSeats,
      },
    ],
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/bookings/:bookingNumber/rebook',
    name: 'AfterSaleRebook',
    component: AfterSaleRebook,
    props: (route: RouteLocationNormalized) => ({
      bookingNumber: route.params.bookingNumber,
    }),
  },
  {
    path: '/bookings/:bookingNumber/avbestill',
    name: 'AfterSaleCancel',
    component: AfterSaleCancel,
    props: (route: RouteLocationNormalized) => ({
      bookingNumber: route.params.bookingNumber,
    }),
  },
  {
    path: '/get-ticket/:bookingNumber?',
    name: 'getTicket',
    component: GetTicketPage,
    props: (route: RouteLocationNormalized) => ({
      bookingNumber: route.params.bookingNumber,
    }),
  },
  {
    path: '/ticket/:bookingNumber',
    name: 'ticketPage',
    component: TicketPage,
    props: (route: RouteLocationNormalized) => ({
      bookingNumber: route.params.bookingNumber,
    }),
  },
  {
    path: '/ticket',
    name: 'ticket',
    component: TicketPage,
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.VITE_BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    } else {
      return { top: 0 }
    }
  },
})

router.beforeEach((to, _, next) => {
  const userStore = useUserStore()
  const authStore = useAuthStore()

  if (to.meta.requiresAuth) {
    // Allow access if user is logged in OR has a valid booking token for routes that accept it
    if (
      userStore.isLoggedIn ||
      (to.meta.allowBookingToken &&
        authStore.isBookingToken &&
        authStore.isTokenValid)
    ) {
      next()
    } else {
      next({ name: 'Login' })
    }
  } else if (to.name === 'Login' && userStore.isLoggedIn) {
    next({ name: 'UserInfo' })
  } else if (to.name === 'PersonalInformation') {
    if (to.query.isRebooking || userStore.isLoggedIn) {
      next({ 
        name: 'payment',
        query: to.query // Preserve all query parameters
      })
    } else {
      next()
    }
  } else {
    next()
  }
})

export default router
